import React, { useState } from 'react';
import { CreditCard, Plus } from 'lucide-react';
import { CompanySubscriptionResource } from '../types';
import CompanySubscriptionCard from './CompanySubscriptionCard';
import LoadingSpinner from './LoadingSpinner';
import AssignSubscriptionDialog from './AssignSubscriptionDialog';
import { useAuth } from '../contexts/AuthContext';

interface CompanySubscriptionsListProps {
  subscriptions: CompanySubscriptionResource[];
  loading: boolean;
  companyId: string;
  onSubscriptionAssigned: () => void;
}

const CompanySubscriptionsList: React.FC<CompanySubscriptionsListProps> = ({ 
  subscriptions, 
  loading,
  companyId,
  onSubscriptionAssigned
}) => {
  const { canManage } = useAuth();
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  // Debug logging
  console.log('CompanySubscriptionsList render:', { subscriptions, loading, type: typeof subscriptions, isArray: Array.isArray(subscriptions) });
  
  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-8">
        <LoadingSpinner size="md" text="Loading subscriptions..." />
      </div>
    );
  }

  // Ensure subscriptions is always an array
  const subscriptionsArray = Array.isArray(subscriptions) ? subscriptions : [];
  console.log('Subscriptions array:', subscriptionsArray);

  if (subscriptionsArray.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions found</h3>
        <p className="mt-1 text-sm text-gray-500">
          This company has no active subscriptions.
        </p>
        {canManage && (
          <button
            onClick={() => setShowAssignDialog(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Assign Subscription
          </button>
        )}
        {canManage && showAssignDialog && (
          <AssignSubscriptionDialog
            isOpen={showAssignDialog}
            onClose={() => setShowAssignDialog(false)}
            companyId={companyId}
            onSubscriptionAssigned={() => {
              setShowAssignDialog(false);
              onSubscriptionAssigned();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {canManage && (
        <div className="mb-6">
          <button
            onClick={() => setShowAssignDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Assign Subscription
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {subscriptionsArray.map((subscription) => (
          <CompanySubscriptionCard key={subscription.id} subscription={subscription} />
        ))}
      </div>

      {showAssignDialog && (
        <AssignSubscriptionDialog
          isOpen={showAssignDialog}
          onClose={() => setShowAssignDialog(false)}
          companyId={companyId}
          onSubscriptionAssigned={() => {
            setShowAssignDialog(false);
            onSubscriptionAssigned();
          }}
        />
      )}
    </>
  );
};

export default CompanySubscriptionsList;
